import * as Sentry from '@sentry/browser';
import { saveResponseAnalytics, saveResponses, setAssortment } from '../api';
import { trackOnboardingAnswers } from '../utils/heap';
import { Answers } from './types';
import { mapAnalyticsRequest, mapAnswers } from './utils';

export const BOOST_TRIAL_VERSION = 'creator_onboarding';
export const PHONE_NUMBER_QUESTION_ID = 'phone_number_pp';

export const saveInDatabase = async (
    organizationId: string,
    answers: Answers,
): Promise<void> => {
    return saveResponses(organizationId, mapAnswers(answers));
};

export const saveInAnalytics = async (
    userId: string,
    answers: Answers,
): Promise<any> => {
    const requests: Promise<void>[] = [];

    Object.keys(answers)
        // filter out phone number answer and any null answer values
        .filter((questionId: string) => {
            return (
                questionId !== PHONE_NUMBER_QUESTION_ID &&
                answers[questionId] !== null
            );
        })
        .forEach((questionId) => {
            requests.push(
                saveResponseAnalytics(
                    mapAnalyticsRequest(
                        answers[questionId][0],
                        userId,
                        questionId,
                    ),
                ),
            );
        });

    return Promise.allSettled(requests);
};

const monitorFailedRequests = (rejections: Array<PromiseRejectedResult>) => {
    if (rejections.length > 0) {
        rejections.forEach((failed) => {
            Sentry.captureException(
                failed.reason?.error || 'error creating profile',
            );
        });
    }
};

export const storeOnboardingResponses = async (
    userId: string,
    organizationId: string,
    answers: Answers,
): Promise<void> => {
    try {
        await setAssortment(organizationId);
    } catch (e) {
        return Promise.reject(e);
    }

    try {
        const result = await Promise.allSettled([
            saveInDatabase(organizationId, answers),
            saveInAnalytics(userId, answers),
        ]);

        const rejections = result.filter(
            ({ status }) => status === 'rejected',
        ) as Array<PromiseRejectedResult>;

        monitorFailedRequests(rejections);
        trackOnboardingAnswers(answers);

        return Promise.resolve();
    } catch (e) {
        return Promise.reject(e);
    }
};

export const storeOnboardingResponsesWithSavedAnswers = async (
    userId: string,
    organizationId: string,
) => {
    const rawStoredAnswers = window?.localStorage?.getItem('ob_answers');
    const answers = rawStoredAnswers ? JSON.parse(rawStoredAnswers) : {};

    return await storeOnboardingResponses(userId, organizationId, answers);
};
